import React, { useState, useCallback, useEffect } from "react";
import { Link } from "react-router-dom";
import fullLogo from "../assets/logo.png";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { ChevronRightIcon } from "@radix-ui/react-icons";
import * as Accordion from "@radix-ui/react-accordion";
import * as Collapsible from "@radix-ui/react-collapsible";
import "./header.css";
import "../soothing-theme.css";

const Header = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  const handleScroll = useCallback(() => {
    if (menuOpen) {
      setMenuOpen(false);
    }
  }, [menuOpen]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 931);
    };
    window.addEventListener("resize", handleResize);
    handleResize();
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [menuOpen, handleScroll]);

  return (
    <header className="overlay-header">
      <nav className="container overlay-nav">
        <div className="logo-box">
          <div className="logo">
            <img src={fullLogo} alt="Leclerc Psychological Services Logo" />
          </div>
        </div>

        <div className="menu-box">
          <div className="menu-toggle" onClick={() => setMenuOpen(!menuOpen)}>
            ☰
          </div>
          {isMobile ? (
            <div className={`menu ${menuOpen ? "open" : ""}`}>
              <Accordion.Root type="single" collapsible>
                <Accordion.Item
                  value="home"
                  className="accordion-item common-accordion-font-style"
                >
                  <Link to="/" onClick={() => setMenuOpen(false)}>
                    Home
                  </Link>
                </Accordion.Item>
                <Accordion.Item
                  value="about"
                  className="accordion-item common-accordion-font-style"
                >
                  <Link to="/about" onClick={() => setMenuOpen(false)}>
                    About
                  </Link>
                </Accordion.Item>
                <Accordion.Item
                  value="clinical-services"
                  className="accordion-item"
                >
                  <Accordion.Header>
                    <Accordion.Trigger className="accordion-trigger common-accordion-font-style">
                      Clinical Services <ChevronRightIcon />
                    </Accordion.Trigger>
                  </Accordion.Header>
                  <Accordion.Content>
                    {/* Submenu 1 */}
                    <Collapsible.Root>
                      <Collapsible.Trigger className="accordion-subtrigger common-accordion-font-style">
                        Therapy <ChevronRightIcon />
                      </Collapsible.Trigger>
                      <Collapsible.Content className="accordion-submenu common-accordion-font-style">
                        <Link
                          to="/services/individual-therapy"
                          onClick={() => setMenuOpen(false)}
                          className="common-accordion-font-style"
                        >
                          Individual Therapy
                        </Link>
                        <Link
                          to="/services/dbt"
                          onClick={() => setMenuOpen(false)}
                          className="common-accordion-font-style"
                        >
                          Dialectical Behavioural Therapy (DBT)
                        </Link>
                        <Link
                          to="/services/couple-therapy"
                          onClick={() => setMenuOpen(false)}
                          className="common-accordion-font-style"
                        >
                          Couple Therapy
                        </Link>
                      </Collapsible.Content>
                    </Collapsible.Root>
                    {/* Submenu 2 */}
                    <Collapsible.Root>
                      <Collapsible.Trigger className="accordion-subtrigger common-accordion-font-style">
                        Assessment <ChevronRightIcon />
                      </Collapsible.Trigger>
                      <Collapsible.Content className="accordion-submenu common-accordion-font-style">
                        <Link
                          to="/services/psychodiagnostic"
                          onClick={() => setMenuOpen(false)}
                          className="common-accordion-font-style"
                        >
                          Psychodiagnostic Assessments
                        </Link>
                        <Link
                          to="/services/adhd"
                          onClick={() => setMenuOpen(false)}
                          className="common-accordion-font-style"
                        >
                          ADHD Assessments
                        </Link>
                      </Collapsible.Content>
                    </Collapsible.Root>
                  </Accordion.Content>
                  <Accordion.Content className="consultation-trigger">
                    <Link
                      to="/services/consultation"
                      onClick={() => setMenuOpen(false)}
                    >
                      Consultation
                    </Link>
                  </Accordion.Content>
                </Accordion.Item>
                <Accordion.Item
                  value="about"
                  className="accordion-item common-accordion-font-style"
                >
                  <Link
                    to="/contact"
                    onClick={() => setMenuOpen(false)}
                    className="common-accordion-font-style"
                  >
                    Contact
                  </Link>
                </Accordion.Item>
              </Accordion.Root>
            </div>
          ) : (
            <div className={`menu ${menuOpen ? "open" : ""}`}>
              <Link to="/" onClick={() => setMenuOpen(false)}>
                Home
              </Link>
              <Link to="/about" onClick={() => setMenuOpen(false)}>
                About
              </Link>

              <DropdownMenu.Root>
                <DropdownMenu.Trigger className="dropdown-trigger">
                  Clinical Services
                </DropdownMenu.Trigger>
                <DropdownMenu.Portal>
                  <DropdownMenu.Content
                    className="dropdown-menu"
                    sideOffset={5}
                  >
                    <DropdownMenu.Sub>
                      <DropdownMenu.SubTrigger className="dropdown-subtrigger">
                        Therapy <ChevronRightIcon />
                      </DropdownMenu.SubTrigger>
                      <DropdownMenu.Portal>
                        <DropdownMenu.SubContent className="dropdown-submenu">
                          <DropdownMenu.Item asChild>
                            <Link to="/services/individual-therapy">
                              Individual Therapy
                            </Link>
                          </DropdownMenu.Item>
                          <DropdownMenu.Item asChild>
                            <Link to="/services/dbt">
                              Dialectical Behavioural Therapy (DBT)
                            </Link>
                          </DropdownMenu.Item>
                          <DropdownMenu.Item asChild>
                            <Link to="/services/couple-therapy">
                              Couple Therapy
                            </Link>
                          </DropdownMenu.Item>
                        </DropdownMenu.SubContent>
                      </DropdownMenu.Portal>
                    </DropdownMenu.Sub>

                    <DropdownMenu.Sub>
                      <DropdownMenu.SubTrigger className="dropdown-subtrigger">
                        Assessment <ChevronRightIcon />
                      </DropdownMenu.SubTrigger>
                      <DropdownMenu.Portal>
                        <DropdownMenu.SubContent className="dropdown-submenu">
                          <DropdownMenu.Item asChild>
                            <Link to="/services/psychodiagnostic">
                              Psychodiagnostic Assessments
                            </Link>
                          </DropdownMenu.Item>
                          <DropdownMenu.Item asChild>
                            <Link to="/services/adhd">ADHD Assessments</Link>
                          </DropdownMenu.Item>
                        </DropdownMenu.SubContent>
                      </DropdownMenu.Portal>
                    </DropdownMenu.Sub>

                    <DropdownMenu.Sub>
                      <DropdownMenu.Item asChild>
                        <Link to="/services/consultation">Consultation</Link>
                      </DropdownMenu.Item>
                    </DropdownMenu.Sub>
                  </DropdownMenu.Content>
                </DropdownMenu.Portal>
              </DropdownMenu.Root>

              <Link to="/contact" onClick={() => setMenuOpen(false)}>
                Contact
              </Link>
            </div>
          )}
        </div>
      </nav>
    </header>
  );
};

export default Header;
